import { Injectable } from '@angular/core';
import * as codeExport from '../../../assets/codeexport.json';

export type smartCommentSingleType = "block" | "line";
export type smartCommentType = `${smartCommentSingleType}-${smartCommentSingleType}-${smartCommentSingleType}` | `${smartCommentSingleType}-${smartCommentSingleType}` | smartCommentSingleType;
export type CodeCompletion = {
  label: string,
  type?: string,
  info?: string,
  apply?: string,
  detail?: string
};
export type CodeToolbar = {
  header: string,
  data: {
    label: string,
    info: string,
    code: string
  }[]
};
export type RagaiDataCode = {
  method: string,
  description: string,
  code: string
}
export type RagaiData = {
  name: string,
  data: RagaiDataCode[]
}
export type CodeExport = {
  ragai: RagaiData[]
}
export type customCode = {
  code: string,
  info: string
}

@Injectable({
  providedIn: 'root'
})
export class CodecompletionService {
  public codeCompletionOptions: CodeCompletion[] = [];
  public codeToolbarOptions: CodeToolbar[] = [];

  public initCodeCompletion(skills: string[], stages: string[], variables: string[], customCode: customCode[] = [], confRequired: boolean = true, ragaiRequired: boolean = true, botapiRequired: boolean = false) {
    this.codeCompletionOptions = [];
    this.codeToolbarOptions = [];
    if (confRequired) this.getConfEntrys();
    if (skills.length > 0) this.getSkillEntrys(skills);
    if (stages.length > 0) this.getStageEntrys(stages);
    if (variables.length > 0) this.getVariableEntrys(variables);
    if (customCode.length > 0) this.getCustomCompletion(customCode);
    this.getDocbasedCodeCompletion(ragaiRequired, botapiRequired);
  }

  private getConfEntrys() {
    this.codeCompletionOptions.push({ label: "conf.botName", info: "Name des Bots" });
    this.codeCompletionOptions.push({ label: "conf.botColor", info: "Farbkonfiguration" });
    this.codeCompletionOptions.push({ label: "conf.triggerHistoryItemId", info: "ID der letzten Bot-Nachricht" });
    this.codeCompletionOptions.push({ label: "conf.triggerHistoryItemContent", info: "Inhalt der letzten Bot-Nachricht" });
    this.codeCompletionOptions.push({ label: "conf.triggerHistoryItemOpenAiThreadId", info: "ID seitens OpenAi der letzten Bot-Nachricht" });
  }

  private getSkillEntrys(skills: string[]) {
    for (const skill of skills) {
      this.codeCompletionOptions.push({ label: "'" + skill + "'", type: "text", info: "Skill-Name: '" + skill + "'" });
      this.codeCompletionOptions.push({ label: "skill." + skill + "_count", type: "variable", info: "Skill-Ausführungszahl '" + skill + "'" });
      this.codeCompletionOptions.push({ label: "skill." + skill, type: "variable", info: "Skill Status '" + skill + "'" });
      this.codeCompletionOptions.push({ label: skill, type: "keyword", detail: "(Aktivieren)", apply: "RAGAI.enableSkills(['" + skill + "']);" });
      this.codeCompletionOptions.push({ label: skill, type: "keyword", detail: "(Deaktiveren)", apply: "RAGAI.disableSkills(['" + skill + "']);" });
      this.codeCompletionOptions.push({ label: skill, type: "text", detail: "(Überprüfen)", info: "Skill-Überprüfen ob aktiv '" + skill + "'", apply: "if(skill." + skill + "){\n\n}" });
    }
  }

  private getStageEntrys(stages: string[]) {
    for (const stage of stages) {
      this.codeCompletionOptions.push({ label: "'" + stage + "'", type: "text", info: "Stage-Name: '" + stage + "'" });
    }
  }

  private getVariableEntrys(variables: string[]) {
    for (const variable of variables) {
      this.codeCompletionOptions.push({ label: "🧷 vars." + variable + "", type: "", info: variable, apply: "vars." + variable + "" });
    }
  }

  private getCompleteOptionLine(code: string, info: string) {
    const apply = `${code} // ${info}`;
    return { label: '📏 ' + info + ' //... ', type: "", info: code, apply: apply };
  }

  private getCompleteCoderFriendly(code: string, initialInfo: string) {
    let completion = code;
    if (code.startsWith("await ")) {
      completion = code.split("await ").join("");
    }

    if (code.startsWith("var")) {
      completion = code.split("=")[1] ?? "";
      completion = completion.trim();
    }

    return {
      label: '🤖' + completion,
      type: "",
      info: initialInfo,
      apply: code
    };
  }

  private getCompleteOptionBLOCK(code: string, info: string) {
    const apply = `/************************************ \n ${info} \n ************************************/\n` + code + ";\n";
    return { label: '📦[' + info + ']', type: "", info: "(Quellcode-Template)", apply: apply };
  }

  private getCompleteOptionSmart(code: string, info: string) {
    if (code.endsWith(')')) {
      code = code + ";";
    }
    const ret = [];
    // FÜR DIE COOLEN KIDS
    ret.push(this.getCompleteCoderFriendly(code, info));

    if (code.includes("\n")) {
      ret.push(this.getCompleteOptionBLOCK(code, info));
    } else {
      ret.push(this.getCompleteOptionLine(code, info));
    }

    return ret;
  }

  private getDocbasedCodeCompletion(ragaiRequired: boolean, botapiRequired: boolean) {
    if (ragaiRequired) {
      for (const code of codeExport.ragai) {
        this.codeToolbarOptions.push({ header: code.name, data: [] });
        for (const block of code.data) {
          this.codeCompletionOptions.push(...this.getCompleteOptionSmart(block.code, block.description));
          if (block.method.includes('(')) {
            block.method = block.method.slice(0, block.method.lastIndexOf('(')).concat('()');
          }
          this.codeToolbarOptions[this.codeToolbarOptions.length - 1]?.data.push({ label: block.method, info: block.description, code: block.code });
        }
      }
    }
    if (botapiRequired) {
      for (const block of codeExport.botapi) {
        this.codeCompletionOptions.push(...this.getCompleteOptionSmart(block.code, block.description));
      }
    }
  }

  private getCustomCompletion(completions: customCode[]) {
    for (const completion of completions) {
      this.codeCompletionOptions.push(...this.getCompleteOptionSmart(completion.code, completion.info));
    }
  }

}
